import type { WithInherit, Direction } from '@wix/editor-elements-common-utils';
import {
  MenuDisplayMode,
  VerticalDropdownDisplay,
  MenuOrientation,
  MenuOverflow,
  ItemTextAlignment,
  MenuAnimationName,
} from '../../constants';
import type {
  MenuOrientationType,
  IMenuOrientationCssVars,
  MenuDisplayModeType,
  MenuOverflowType,
  MenuVerticalDropdownDisplayType,
  IMenuVerticalDropdownDisplayCssVars,
  IMenuOverflowCssVars,
  MenuJustificationType,
  IMenuJustificationCssVars,
  IMenuDisplayModeCssVars,
  IItemTextAlignmentCssVars,
  MenuAnimationNameType,
  MenuAnimationCssVars,
} from '../../Menu.types';

const horizontalCssVars: IMenuOrientationCssVars = {
  '--container-flex-direction': 'row',
  '--item-wrapper-width': 'unset',
  '--menu-items-row-gap': 'var(--menu-items-cross-axis-gap)',
  '--menu-items-column-gap': 'var(--menu-items-main-axis-gap)',
  '--horizontal-menu-item-divider': 'var(--item-divider)',
  '--vertical-menu-item-divider': 'none',
  '--container-pointer-events': 'initial',
  '--item-wrapper-display': 'block',
  '--horizontal-menu-dropdown-display': 'unset',
  '--vertical-menu-dropdown-display': 'none',
  '--empty-dropdown-item-icon-display': 'unset',
  '--menu-item-wrapper-height': '100%',
  '--item-icon-display': 'var(--horizontal-item-icon-display)',
};

const verticalCssVars: Omit<
  IMenuOrientationCssVars,
  keyof IMenuVerticalDropdownDisplayCssVars
> = {
  '--container-flex-direction': 'column',
  '--item-wrapper-width': '100%',
  '--menu-items-row-gap': 'var(--menu-items-main-axis-gap)',
  '--menu-items-column-gap': 'var(--menu-items-cross-axis-gap)',
  '--horizontal-menu-item-divider': 'none',
  '--vertical-menu-item-divider': 'var(--item-divider)',
  '--container-pointer-events': 'none',
  '--item-wrapper-display': 'var(--item-wrapper-display-alignment)',
  '--horizontal-menu-dropdown-display': 'none',
  '--vertical-menu-dropdown-display': 'unset',
  '--empty-dropdown-item-icon-display': 'none',
  '--menu-item-wrapper-height': 'unset',
};

const verticalAlwaysOpenCssVars: IMenuVerticalDropdownDisplayCssVars = {
  '--item-icon-display': 'none',
  '--vertical-dropdown-visibility': 'unset',
  '--vertical-dropdown-height': 'unset',
};

const verticalExpandCollapsedCssVars: IMenuVerticalDropdownDisplayCssVars = {
  '--item-icon-display': 'unset',
  '--vertical-dropdown-visibility': 'hidden',
  '--vertical-dropdown-height': '0',
  '--vertical-expand-collapse-display': 'none',
};

export const justificationToCssVarsMap: Record<
  MenuJustificationType,
  IMenuJustificationCssVars
> = {
  justify: {
    '--item-width': 'auto',
    '--menu-items-flex-grow': 1,
    '--item-wrapper-display-alignment': 'block',
  },
  none: {
    '--item-width': 'fit-content',
    '--menu-items-flex-grow': 0,
    '--item-wrapper-display-alignment': 'flex',
  },
};

export const orientationToCssVarsMap: Record<
  MenuOrientationType,
  IMenuOrientationCssVars
> = {
  [MenuOrientation.Horizontal]: horizontalCssVars,
  [MenuOrientation.Vertical]: verticalCssVars as IMenuOrientationCssVars,
};

export const dropdownDisplayTypeToCssVarMap: Record<
  MenuVerticalDropdownDisplayType,
  IMenuVerticalDropdownDisplayCssVars
> = {
  [VerticalDropdownDisplay.AlwaysOpen]: verticalAlwaysOpenCssVars,
  [VerticalDropdownDisplay.ExpandCollapse]: verticalExpandCollapsedCssVars,
};

export const verticalOverflowCssVars: IMenuOverflowCssVars = {
  'min-width': 'min-content',
};

export const horizontalOverflowToCssVarsMap: Record<
  MenuOverflowType,
  IMenuOverflowCssVars
> = {
  [MenuOverflow.Scroll]: {
    'min-width': 'initial',
    '--container-overflow-x': 'auto',
    '--container-flex-wrap': 'nowrap',
  },
  [MenuOverflow.Wrap]: {
    height: 'auto',
    'min-width': 'min-content',
    '--container-overflow-x': 'visible',
    '--container-flex-wrap': 'wrap',
    '--scroll-button-icon-display': 'none',
  },
};

export const displayModeToCssVarsMap: Record<
  MenuDisplayModeType,
  IMenuDisplayModeCssVars
> = {
  [MenuDisplayMode.Navbar]: {
    '--navbar-display': 'unset',
    '--hamburger-menu-root-display': 'none',
  },
  [MenuDisplayMode.Hamburger]: {
    '--navbar-display': 'none',
    '--hamburger-menu-root-display': 'unset',
  },
};

export const itemTextAlignmentToCssVarsMap: Record<
  string,
  IItemTextAlignmentCssVars
> = {
  [ItemTextAlignment.Justify]: {
    '--item-text-align': 'space-between',
  },
};

const resetAnimationVars = {
  '--item-selected-label-underline-display': 'none',
  '--item-label-bullet-display': 'none',
};

export const animationNameToCssVarsMap: Record<
  MenuAnimationNameType,
  MenuAnimationCssVars
> = {
  [MenuAnimationName.None]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Underline]: {
    ...resetAnimationVars,
    '--item-selected-label-underline-display': 'unset',
  },
  [MenuAnimationName.Wash]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Bullet]: {
    ...resetAnimationVars,
    '--item-label-bullet-display': 'inline-block',
  },
  [MenuAnimationName.Wave]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Bounce]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Tint]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.TextWash]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Wobble]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Brackets]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Float]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Shape]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Expand]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Circle]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Shadow]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.LineRise]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Overline]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.CenterLine]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Skew]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Point]: {
    ...resetAnimationVars,
  },
  [MenuAnimationName.Blur]: {
    ...resetAnimationVars,
  },
};

export const getDirectionCssVars = (direction: WithInherit<Direction>) => ({
  '--direction': direction,
  '--dropdown-menu-direction': direction,
  // TODO: should be reworked when direction in containers will be implemented
  '--scroll-button-transform': direction === 'rtl' ? 'scaleX(-1)' : 'scaleX(1)',
});
